import { createBrowserRouter, Navigate } from "react-router";
import type { ReactNode } from "react";
import { AuthGuard } from "./components/AuthGuard";
import { Layout } from "./components/Layout";
import { MeProvider, useMe } from "./components/MeProvider";
import { LoginScreen } from "./screens/LoginScreen";
import { DashboardScreen } from "./screens/DashboardScreen";
import { QuestionsScreen } from "./screens/QuestionsScreen";
import { QuestionEditorScreen } from "./screens/QuestionEditorScreen";
import { TopicsScreen } from "./screens/TopicsScreen";
import { ActivityScreen } from "./screens/ActivityScreen";
import { BooksScreen } from "./screens/pqx/BooksScreen";
import { BookScreen } from "./screens/pqx/BookScreen";
import { ReviewScreen } from "./screens/pqx/ReviewScreen";

/**
 * Sends someone to a screen they can use. The backend enforces access; this
 * only stops a product manager landing on content screens that would 403.
 */
function Allow({ need, children }: { need: "content" | "insights"; children: ReactNode }) {
  const { me, loading } = useMe();
  if (loading) return <p className="text-text-muted">Loading…</p>;
  if (me && !me.can[need]) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const content = (element: ReactNode) => <Allow need="content">{element}</Allow>;

export const router = createBrowserRouter([
  { path: "/login", element: <LoginScreen /> },
  {
    element: (
      <AuthGuard>
        <MeProvider>
          <Layout />
        </MeProvider>
      </AuthGuard>
    ),
    children: [
      // Open to everyone signed in: the dashboard shows the tabs a role allows.
      { path: "/", element: <DashboardScreen /> },
      { path: "/questions", element: content(<QuestionsScreen />) },
      { path: "/questions/new", element: content(<QuestionEditorScreen />) },
      { path: "/questions/:id", element: content(<QuestionEditorScreen />) },
      { path: "/topics", element: content(<TopicsScreen />) },
      // Old link: uploading now lives on the Books page.
      { path: "/upload", element: <Navigate to="/books?tab=upload" replace /> },
      { path: "/activity", element: content(<ActivityScreen />) },
      { path: "/books", element: content(<BooksScreen />) },
      { path: "/books/:bookId", element: content(<BookScreen />) },
      { path: "/books/:bookId/review/:questionId", element: content(<ReviewScreen />) },
      // Insights now live on the dashboard; keep old links working.
      { path: "/insights", element: <Navigate to="/?tab=overview" replace /> },
    ],
  },
]);
